import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
    AvatarGroup,
    Button,
    Chip,
    Dialog,
    LinearProgress,
    linearProgressClasses,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import { useFilters, useExpanded, useGlobalFilter, useRowSelect, useSortBy, useTable, usePagination } from 'react-table';

// project-imports
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';
import { PopupTransition } from 'components/@extended/Transitions';
import {
    CSVExport,
    HeaderSort,
    SortingSelect,
    TablePagination
} from 'components/third-party/ReactTable';

import { renderFilterTypes, GlobalFilter } from 'utils/react-table';

// assets
import { Add, Edit, Eye, Graph, Trash } from 'iconsax-react';
import { ThemeMode } from 'config';
import useAuth from 'hooks/useAuth';
import { styled } from '@mui/system';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import useBreadcrumbStore from 'store/useBreadcrumbStore';
import { formatID, shortDate } from 'utils/appUtils';
import SponsorView from 'pages/sponsor/SponsorView';
import { useNavigate } from 'react-router';
import graphon from 'assets/images/lead/graphon.png'
import graphoff from 'assets/images/lead/graphoff.png'
import AlertDashboardDelete from './AlertDashboardDelete';

const avatarImage = require.context('assets/images/users', true);

// ==============================|| REACT TABLE ||============================== //

/*const StatusChip = ({ value }) => {
    switch (value) {
        case 'FINISHED':
            return <Chip label="Finalizado" className="bg-[#06BF7B21] text-green-500" size="small" />
        case 'ACTIVE':
            return <Chip label="Activo" className="bg-[#06BF7B21] text-green-500" size="small" />
        case 'INACTIVE':
            return <Chip label="Inactivo" className="bg-[#FF414121] text-[#FF4141]" size="small" />
        default:
            return <Chip label="Indefinido" className="bg-[#9399AA21] text-slate-800" size="small" />
    }
}*/

const StatusChip = ({ value }) => {
    switch (value) {
        case 'FINALIZADO':
            return <Chip label="Terminado" className="bg-[#06BF7B21] text-green-500" size="small" />
        case 'PROCESANDO':
            return <Chip label="Procesando" className="bg-[#E26205] text-orange-500 bg-opacity-20" size="small" />
            case 'ACTIVO':
            return <Chip label="Activo" className="bg-[#06BF7B21] text-green-500" size="small" />
        case 'INACTIVE':
            return <Chip label="Inactivo" className="bg-[#FF414121] text-[#FF4141]" size="small" />
        default:
            return <Chip label="Indefinido" className="bg-[#9399AA21] text-slate-800" size="small" />
    }
}

const progressValue = (value) => {
    switch (value) {
        case 'FINALIZADO':
            return 100
        case 'PROCESANDO':
            return 50
            case 'ACTIVO':
            return 100
        case 'INACTIVE':
            return 0
        default:
            return 0
    }
}

const StatusBar = ({ progress }) => {
    return (
        <LinearProgress
            variant="determinate"
            value={progress || 0}
            sx={{
                height: 8, // Altura del LinearProgress
                borderRadius: 5,
                backgroundColor: '#D9D9D9',
                '& .MuiLinearProgress-bar': {
                    backgroundColor: '#E26205',
                }
            }}
        />
    );
}

function ReactTable({ columns, data, renderRowSubComponent, handleAdd }) {
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

    const filterTypes = useMemo(() => renderFilterTypes, []);
    const sortBy = { id: 'id', desc: false };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        setHiddenColumns,
        allColumns,
        visibleColumns,
        rows,
        page,
        gotoPage,
        setPageSize,
        state: { globalFilter, selectedRowIds, pageIndex, pageSize, expanded },
        preGlobalFilteredRows,
        setGlobalFilter,
        setSortBy,
        selectedFlatRows
    } = useTable(
        {
            columns,
            data,
            filterTypes,
            initialState: { pageIndex: 0, pageSize: 10, hiddenColumns: [], sortBy: [sortBy] }
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );

    return (
        <>
            {/*<TableRowSelection selected={Object.keys(selectedRowIds).length} />*/}
            <Stack spacing={3}>
                <Stack
                    direction={matchDownSM ? 'column' : 'row'}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ p: 3, pb: 0 }}
                >
                    <GlobalFilter preGlobalFilteredRows={preGlobalFilteredRows} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                    <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={2}>
                        <SortingSelect sortBy={sortBy.id} setSortBy={setSortBy} allColumns={allColumns} />
                        <Button variant="contained" startIcon={<Add />} onClick={handleAdd} size="small" className='bg-orange-500 hover:bg-orange-400'>
                            Crear Dashboard
                        </Button>
                        <CSVExport data={selectedFlatRows.length > 0 ? selectedFlatRows.map((d) => d.original) : data} filename={'sponsors-list.csv'} />
                    </Stack>
                </Stack>
                <Table {...getTableProps()}>
                    <TableHead>
                        {headerGroups.map((headerGroup) => (
                            <TableRow key={headerGroup} {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
                                {headerGroup.headers.map((column) => (
                                    <TableCell key={column} {...column.getHeaderProps([{ className: column.className }])}>
                                        <HeaderSort column={column} sort />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {page.length > 0 ? (
                            page.map((row, i) => {
                                prepareRow(row);
                                const rowProps = row.getRowProps();

                                return (
                                    <Fragment key={i}>
                                        <TableRow
                                            {...row.getRowProps()}
                                            // onClick={() => {
                                            //     row.toggleRowSelected();
                                            // }}
                                            sx={{ cursor: 'pointer', bgcolor: row.isSelected ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit' }}
                                        >
                                            {row.cells.map((cell) => (
                                                <TableCell key={cell} {...cell.getCellProps([{ className: cell.column.className }])}>
                                                    {cell.render('Cell')}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        {row.isExpanded && renderRowSubComponent({ row, rowProps, visibleColumns, expanded })}
                                    </Fragment>
                                );
                            })
                        ) : (
                            // Renderiza esta fila si no hay datos
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No hay dashboards disponibles
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
                            <TableCell sx={{ p: 2, py: 3 }} colSpan={9}>
                                <TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageSize={pageSize} pageIndex={pageIndex} />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Stack>
        </>
    );
}
const DashboardList = ({ boards, refresh }) => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    //const [sponsors, setSponsors] = useState([]);
    const [open, setOpen] = useState(false);
    const [customer, setCustomer] = useState(null);
    const [customerDeleteId, setCustomerDeleteId] = useState('');
    const [add, setAdd] = useState(false);
    const { deleteDashboard } = useAuth();
    const [sponsorDeleteName, setSponsorDeleteName] = useState("")
    const setBreadcrumbs = useBreadcrumbStore(state => state.setBreadcrumbs);
    const navigate = useNavigate()

    useEffect(() => {
        setBreadcrumbs([
            { label: 'Inicio', path: '/' },
            { label: 'Lista de Dashboards', path: '/lead' },
        ]);

        return () => setBreadcrumbs([]);
    }, [setBreadcrumbs]);

    /*const addSponsor = (sponsor) => {
        // console.log([...sponsors, sponsor])
        setSponsors([...sponsors, sponsor])
    }

    const updateSponsor = (updatedSponsor) => {
        setSponsors(prevSponsors =>
            prevSponsors.map(sponsor =>
                sponsor.uuid === updatedSponsor.uuid ? updatedSponsor : sponsor
            )
        );
    }*/

    const handleAdd = () => {
        navigate('/lead-upload');
    };

    const handleEdit = (campaignId) => {
        if (campaignId) {
            navigate(`/lead-upload/${campaignId}`);
        }
    };

    const openDashboard = (campaignId, active = false) => {
        if (active) {
            navigate(`/lead/board/${campaignId}`);
        }
    };

    const getUuidById = (id) => {
        const foundObject = boards.find(item => item.id === id);
        return foundObject ? foundObject.uuid : "";
    };
    const getObjectById = (id) => {
        return boards.find(item => item.id === id);
    };

    const getObjectByUuid = (uuid) => {
        return boards.find(item => item.uuid === uuid);
    };

    const handleClose = (action) => {
        setOpen(!open);
        if (action) {
            deleteDashboard(getUuidById(customerDeleteId))
                .then((response) => {
                    refresh()
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: 'Dashboard eliminado correctamente.',
                            variant: 'alert',
                            alert: {
                                color: 'success'
                            },
                            close: true
                        })
                    );
                }).catch((error) => {
                    console.error(error)
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: 'Error al eliminar dashboard',
                            variant: 'alert',
                            alert: {
                                color: 'error'
                            },
                            close: true
                        })
                    );
                }).finally(() => {
                    //setLoadingData(false)
                })
        }
    };

    const columns = useMemo(() => [
        /*{
            title: 'Row Selection',
            Header: ({ getToggleAllPageRowsSelectedProps }) => <IndeterminateCheckbox indeterminate {...getToggleAllPageRowsSelectedProps()} />,
            accessor: 'selection',
            Cell: ({ row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
            disableSortBy: true
        },*/
        {
            Header: '#',
            accessor: 'id',
            className: 'cell-center',
            Cell: ({ row }) => {
                return (
                    <Typography variant="body1">{formatID(row.values.id)}</Typography>
                );
            }
        },
        {
            Header: 'CAMPAÑA',
            accessor: 'name',
            Cell: ({ row }) => {
                const { original: values } = row;
                return (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar alt="Avatar 1" size="sm" src={values.logo} />
                        <Stack spacing={0}>
                            <Typography variant="subtitle1">{values.name}</Typography>
                        </Stack>
                    </Stack>
                );
            }
        },
        {
            Header: 'PAIS',
            accessor: 'country.common_name'
        },
        {
            Header: 'FECHA DE CREACIÓN',
            accessor: 'created_at',
            Cell: ({ row }) => {
                const { original: values } = row;
                return (
                    <div className='flex items-center justify-center'>
                        <Typography variant="body1">{shortDate(values.created_at)}</Typography>
                    </div>
                );
            }
        },
        {
            Header: 'ESTADO',
            accessor: 'lead_manager_process',
            Cell: ({ value }) => {
                return (
                    <div className='flex flex-col space-y-4'>
                        <StatusChip value={value} />
                        <StatusBar progress={progressValue(value)} />
                    </div>

                );
            }
        },
        {
            Header: 'DASHBOARD',
            accessor: 'lead_manager_graph_url',
            Cell: ({ row }) => {
                const { values } = row;
                return (
                    <div className='flex justify-center items-center'>
                        <Avatar size='sm' src={values.lead_manager_graph_url ? graphon : graphoff} onClick={() => openDashboard(values.uuid, values.lead_manager_graph_url)} />
                    </div>
                );
            }
        },
        {
            Header: 'ACCIONES',
            accessor: 'uuid',
            className: 'cell-center',
            disableSortBy: true,
            Cell: ({ row }) => {
                const collapseIcon = row.isExpanded ? <Add style={{ color: theme.palette.error.main, transform: 'rotate(45deg)' }} /> : <Eye />;
                return (
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
                        {/*<Tooltip
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                                        opacity: 0.9
                                    }
                                }
                            }}
                            title="View"
                        >
                            <IconButton
                                color="secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    row.toggleRowExpanded();
                                }}
                            >
                                {collapseIcon}
                            </IconButton>
                            </Tooltip>*/}
                        <Tooltip
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                                        opacity: 0.9
                                    }
                                }
                            }}
                            title="Edit"
                        >
                            <IconButton
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCustomer(row.values);
                                    handleEdit(row.values.uuid);
                                }}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                                        opacity: 0.9
                                    }
                                }
                            }}
                            title="Delete"
                        >
                            <IconButton
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClose();
                                    setCustomerDeleteId(row.values.id);
                                    setSponsorDeleteName(row.values.name);
                                }}
                            >
                                <Trash />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            }
        }
    ], [theme]);


    const renderRowSubComponent = useCallback(({ row }) => <SponsorView data={boards[Number(row.id)]} />, [boards]);


    const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
        height: 10,
        borderRadius: 5,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
        },
        [`& .${theme.palette.primary}`]: {
            borderRadius: 5,
            backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
        },
    }));

    return (
        <MainCard content={false}>
            <ScrollX>
                <ReactTable columns={columns} data={boards} handleAdd={handleAdd} renderRowSubComponent={renderRowSubComponent} />
                {boards.length <= 0 && <BorderLinearProgress variant="indeterminate" value={50} />}
            </ScrollX>
            <AlertDashboardDelete title={customerDeleteId} open={open} handleClose={handleClose} name={sponsorDeleteName} />
            <Dialog
                maxWidth="sm"
                TransitionComponent={PopupTransition}
                keepMounted
                fullWidth
                onClose={handleAdd}
                open={add}
                sx={{ '& .MuiDialog-paper': { p: 0 }, transition: 'transform 225ms' }}
                aria-describedby="alert-dialog-slide-description"
            >
            </Dialog>
        </MainCard>
    );
}

export default DashboardList;